import { useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, RotateCcw, AlertTriangle, FileText, FolderTree, Loader2, X } from 'lucide-react';
import { listTrashed, restoreEntity, purgeEntity } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import ConfirmTrashModal from '../../shared/ui/ConfirmTrashModal';

const ENTITY_ICONS = {
  doc_directory: FolderTree,
  builder_page: FileText,
  doc_adr: FileText,
};

function countChildren(item, allItems) {
  if (item.entity_type !== 'taxonomy_term') return 0;
  const dirId = item.id;
  const childDirs = allItems.filter(i => i.entity_type === 'taxonomy_term' && i.parent_id === dirId);
  const childPages = allItems.filter(i => i.entity_type === 'node' && i.directory_id === dirId);
  let total = childDirs.length + childPages.length;
  for (const child of childDirs) {
    total += countChildren(child, allItems);
  }
  return total;
}

function TrashItem({ item, childCount, onRestore, onPurge, isAdmin, restoring, purging }) {
  const Icon = ENTITY_ICONS[item.bundle] || FileText;
  const deletedDate = new Date(item.deleted_at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const isBusy = restoring === item.id || purging === item.id;
  const isDir = item.entity_type === 'taxonomy_term';

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors group">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center text-stone-400">
        <Icon size={16} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-stone-800 truncate">{item.name}</span>
          {item.is_root && (
            <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
              Espace
            </span>
          )}
          {isDir && !item.is_root && (
            <span className="text-[10px] font-semibold text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">
              Répertoire
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-stone-400 mt-0.5">
          <span>Supprimé le {deletedDate}</span>
          {item.author && <span>par {item.author.name}</span>}
          {childCount > 0 && (
            <span className="text-stone-300">
              · {childCount} élément{childCount > 1 ? 's' : ''} inclus
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onRestore(item)}
          disabled={isBusy}
          className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
          aria-label="Restaurer"
          title="Restaurer"
        >
          {restoring === item.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
        </button>

        {isAdmin && (
          <button
            onClick={() => onPurge(item)}
            disabled={isBusy}
            className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
            aria-label="Supprimer définitivement"
            title="Supprimer définitivement"
          >
            {purging === item.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DocTrashList() {
  const { isAdmin } = useAuth();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [purging, setPurging] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const user = isAdmin ? 'all' : 'me';
      const data = await listTrashed('doc', user);
      setAllItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const topLevel = useMemo(() => {
    const trashedDirIds = new Set(
      allItems.filter(i => i.entity_type === 'taxonomy_term').map(i => i.id)
    );
    return allItems.filter(item => {
      if (item.entity_type === 'taxonomy_term') {
        return !trashedDirIds.has(item.parent_id);
      }
      if (item.entity_type === 'node') {
        return !trashedDirIds.has(item.directory_id);
      }
      return true;
    });
  }, [allItems]);

  const childCounts = useMemo(() => {
    const map = new Map();
    for (const item of topLevel) {
      map.set(item.id, countChildren(item, allItems));
    }
    return map;
  }, [topLevel, allItems]);

  const handleRestore = useCallback((item) => {
    const isDir = item.entity_type === 'taxonomy_term';
    const childCount = childCounts.get(item.id) || 0;
    const label = isDir
      ? (item.is_root ? `l'espace « ${item.name} »` : `le répertoire « ${item.name} »`)
      : `la page « ${item.name} »`;
    const desc = isDir && childCount > 0
      ? `${item.is_root ? 'Cet espace' : 'Ce répertoire'} et ses ${childCount} éléments seront restaurés.`
      : `${label} sera restauré.`;

    setConfirmAction({
      type: 'restore',
      item,
      title: `Restaurer ${label} ?`,
      description: desc,
      confirmLabel: 'Restaurer',
    });
  }, [childCounts]);

  const handlePurge = useCallback((item) => {
    const isDir = item.entity_type === 'taxonomy_term';
    const childCount = childCounts.get(item.id) || 0;
    const label = isDir
      ? (item.is_root ? `l'espace « ${item.name} »` : `le répertoire « ${item.name} »`)
      : `la page « ${item.name} »`;
    const desc = isDir && childCount > 0
      ? `${item.is_root ? 'Cet espace' : 'Ce répertoire'} et ses ${childCount} éléments seront supprimés définitivement. Cette action est irréversible.`
      : 'Cette action est irréversible. Les données seront perdues.';

    setConfirmAction({
      type: 'purge',
      item,
      title: `Supprimer définitivement ${label} ?`,
      description: desc,
      confirmLabel: 'Supprimer définitivement',
    });
  }, [childCounts]);

  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;
    const { type, item } = confirmAction;
    const setProgress = type === 'restore' ? setRestoring : setPurging;
    setProgress(item.id);
    setActionError(null);
    try {
      if (type === 'restore') {
        await restoreEntity(item.entity_type, item.bundle, item.id);
      } else {
        await purgeEntity(item.entity_type, item.bundle, item.id);
      }
      setConfirmAction(null);
      await fetchItems();
    } catch (err) {
      console.error(`Trash action failed:`, err);
      setConfirmAction(null);
      setActionError(err.message);
      setTimeout(() => setActionError(null), 6000);
    } finally {
      setProgress(null);
    }
  }, [confirmAction, fetchItems]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-stone-300" />
        <span className="text-sm text-stone-400">Chargement de la corbeille...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 px-6">
        <AlertTriangle size={24} className="text-amber-500" />
        <p className="text-sm text-stone-500 text-center">{error}</p>
        <button onClick={fetchItems} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          Réessayer
        </button>
      </div>
    );
  }

  if (topLevel.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 px-6">
        <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
          <Trash2 size={20} className="text-stone-300" />
        </div>
        <p className="text-sm text-stone-400">La corbeille est vide</p>
      </div>
    );
  }

  const spaces = topLevel.filter(i => i.entity_type === 'taxonomy_term' && i.is_root);
  const directories = topLevel.filter(i => i.entity_type === 'taxonomy_term' && !i.is_root);
  const pages = topLevel.filter(i => i.entity_type === 'node');

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {actionError && (
        <div className="alert alert-danger items-center mb-4">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-auto p-0.5 hover:bg-red-100 rounded"><X size={14} /></button>
        </div>
      )}
      <div className="flex items-center gap-3 mb-6">
        <Trash2 size={20} className="text-stone-400" />
        <h1 className="text-lg font-semibold text-stone-900">Corbeille</h1>
        <span className="badge badge-neutral">
          {topLevel.length} élément{topLevel.length > 1 ? 's' : ''}
        </span>
        {allItems.length !== topLevel.length && (
          <span className="text-xs text-stone-300">
            ({allItems.length} au total avec cascade)
          </span>
        )}
      </div>

      <p className="text-xs text-stone-400 mb-4">
        Les éléments sont conservés 90 jours avant suppression automatique.
        Restaurer un espace restaure également tout son contenu.
      </p>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {spaces.map((item) => (
          <TrashItem
            key={`${item.entity_type}-${item.id}`}
            item={item}
            childCount={childCounts.get(item.id) || 0}
            onRestore={handleRestore}
            onPurge={handlePurge}
            isAdmin={isAdmin}
            restoring={restoring}
            purging={purging}
          />
        ))}

        {directories.length > 0 && (
          <>
            <div className="px-4 py-2 bg-stone-50 text-xs font-medium text-stone-400 uppercase tracking-wider">
              Répertoires
            </div>
            {directories.map((item) => (
              <TrashItem
                key={`${item.entity_type}-${item.id}`}
                item={item}
                childCount={childCounts.get(item.id) || 0}
                onRestore={handleRestore}
                onPurge={handlePurge}
                isAdmin={isAdmin}
                restoring={restoring}
                purging={purging}
              />
            ))}
          </>
        )}

        {pages.length > 0 && (
          <>
            {(spaces.length > 0 || directories.length > 0) && (
              <div className="px-4 py-2 bg-stone-50 text-xs font-medium text-stone-400 uppercase tracking-wider">
                Pages
              </div>
            )}
            {pages.map((item) => (
              <TrashItem
                key={`${item.entity_type}-${item.id}`}
                item={item}
                childCount={0}
                onRestore={handleRestore}
                onPurge={handlePurge}
                isAdmin={isAdmin}
                restoring={restoring}
                purging={purging}
              />
            ))}
          </>
        )}
      </div>

      {confirmAction && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          title={confirmAction.title}
          description={confirmAction.description}
          confirmLabel={confirmAction.confirmLabel}
          loading={confirmAction.type === 'restore' ? restoring === confirmAction.item.id : purging === confirmAction.item.id}
          variant={confirmAction.type === 'purge' ? 'purge' : 'trash'}
        />
      )}
    </div>
  );
}
